import React from 'react';
import {
  BarChartOutlined,
  MemoryOutlined,
  StorageOutlined,
  NetworkCheckOutlined,
  SpeedOutlined,
  AccountTreeOutlined,
  ShowChartOutlined,
  TimelineOutlined,
  GridOnOutlined,
  PictureAsPdfOutlined,
  ImageOutlined,
} from '@mui/icons-material';
import { Trans, useTranslation } from 'react-i18next';
import styles from './_shared.module.scss';

const CopyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" aria-hidden>
    <rect x="128" y="128" width="336" height="336" rx="57" ry="57" strokeLinejoin="round" />
    <path
      d="M383.5 128l.5-24a56.16 56.16 0 00-56-56H112a64.19 64.19 0 00-64 64v216a56.16 56.16 0 0056 56h24"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SYSSTAT_CONFIG = `$ sudo cat /etc/default/sysstat

#
# Default settings for /etc/init.d/sysstat, /etc/cron.d/sysstat
# and /etc/cron.daily/sysstat files
#

# Should sadc collect system activity informations? Valid values
# are "true" and "false". Please do not put other values, they
# will be overwritten by debconf!
ENABLED="true" # <<<<<< default: false`;

const SarGuide = () => {
  const { t } = useTranslation();

  const handleCopy = (text, event) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      textarea.setSelectionRange(0, 99999);
      document.execCommand('copy');
      document.body.removeChild(textarea);

      const button = event.currentTarget;
      button.classList.add(styles.copied);
      setTimeout(() => button.classList.remove(styles.copied), 2000);
    } catch (err) {
      console.error('복사 실패:', err);
    }
  };

  const CommandLine = ({ cmd, large = false }) => (
    <div className={`${styles.commandBlock} ${large ? styles.commandBlockLarge : ''}`}>
      <code>{cmd}</code>
      <button
        className={styles.copyButton}
        onClick={(event) => handleCopy(cmd, event)}
        aria-label={t('guide.common.copyCommand')}
      >
        <CopyIcon />
      </button>
    </div>
  );

  return (
    <div className={styles.page}>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroGridOverlay} />
        <div className={styles.heroInner}>
          <div>
            <span className={styles.eyebrow}>Performance Visualization</span>
            <h1 className={styles.heroTitle}>
              <span className={styles.brandGradient}>GWSAR</span> Guide
            </h1>
            <p className={styles.heroSubtitle}>
              {t('guide.sar.subtitle')}
            </p>
            <div className={styles.chipGroup}>
              <span className={`${styles.chip} ${styles.chipBrand}`}>
                <strong>RHEL / Rocky / CentOS</strong>&nbsp;7+
              </span>
              <span className={`${styles.chip} ${styles.chipBrand}`}>
                <strong>Ubuntu</strong>&nbsp;16.04+
              </span>
            </div>
          </div>
          <div className={styles.heroBadge}>
            <BarChartOutlined />
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>{t('guide.common.howToUse')}</span>
          <h2 className={styles.sectionTitle}>{t('guide.sar.stepsTitle')}</h2>
          <p className={styles.sectionLead}>
            {t('guide.sar.stepsLead')}
          </p>
        </header>

        <div className={styles.stepList}>
          {/* Step 1 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>1</div>
            <div className={styles.stepBody}>
              <h3 className={styles.stepTitle}>{t('guide.sar.step1Title')}</h3>
              <p className={styles.stepDesc}>{t('guide.sar.step1Desc')}</p>
              <p className={styles.stepDesc}><strong>{t('guide.sar.rhel7')}</strong></p>
              <CommandLine cmd="sudo yum install sysstat" />
              <p className={styles.stepDesc}><strong>{t('guide.sar.rhel8')}</strong></p>
              <CommandLine cmd="sudo dnf install sysstat" />
              <p className={styles.stepDesc}><strong>Ubuntu / Debian</strong></p>
              <CommandLine cmd={"sudo apt update\nsudo apt install sysstat"} large />
            </div>
          </div>

          {/* Step 2 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>2</div>
            <div className={styles.stepBody}>
              <h3 className={styles.stepTitle}>{t('guide.sar.step2Title')}</h3>
              <p className={styles.stepDesc}>
                <Trans i18nKey="guide.sar.step2Desc" />
              </p>
              <CommandLine cmd={SYSSTAT_CONFIG} large />
            </div>
          </div>

          {/* Step 3 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>3</div>
            <div className={styles.stepBody}>
              <h3 className={styles.stepTitle}>{t('guide.sar.step3Title')}</h3>
              <p className={styles.stepDesc}>
                {t('guide.sar.step3Desc')}
              </p>
              <CommandLine cmd="sar -u 1 3" />
              <p className={styles.stepDesc}>
                <Trans i18nKey="guide.sar.step3Desc2" />
              </p>
              <CommandLine cmd="sar -o sar_data.bin 1 3" />
            </div>
          </div>

          {/* Step 4 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>4</div>
            <div className={styles.stepBody}>
              <h3 className={styles.stepTitle}>{t('guide.sar.step4Title')}</h3>
              <p className={styles.stepDesc}>
                <Trans i18nKey="guide.sar.step4Desc" />
              </p>
              <div className={styles.callout}>
                <strong>CentOS / RHEL</strong>
                <br />
                · {t('guide.sar.text')}: <code>/var/log/sa/sar&lt;{t('guide.sar.number')}&gt;</code>
                <br />
                · {t('guide.sar.binary')}: <code>/var/log/sa/sa&lt;{t('guide.sar.number')}&gt;</code>
              </div>
              <div className={styles.callout}>
                <strong>Ubuntu / Debian</strong>
                <br />
                · {t('guide.sar.text')}: <code>/var/log/sysstat/sar&lt;{t('guide.sar.number')}&gt;</code>
                <br />
                · {t('guide.sar.binary')}: <code>/var/log/sysstat/sa&lt;{t('guide.sar.number')}&gt;</code>
              </div>
            </div>
          </div>

          {/* Step 5 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>5</div>
            <div className={styles.stepBody}>
              <h3 className={styles.stepTitle}>{t('guide.sar.step5Title')}</h3>
              <p className={styles.stepDesc}>
                {t('guide.sar.step5DescBefore')}<code>sa&lt;{t('guide.sar.number')}&gt;</code>{t('guide.sar.step5DescAfter')}
                <strong>&nbsp;Drop SAR binary file here or click to select file&nbsp;</strong>
                {t('guide.sar.step5DescButton')}
              </p>
              <div className={`${styles.callout} ${styles.calloutBrand}`}>
                {t('guide.sar.step5Note')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>Metrics</span>
          <h2 className={styles.sectionTitle}>{t('guide.sar.metricsTitle')}</h2>
        </header>
        <div className={styles.pillGrid}>
          <div className={styles.pillCard}><SpeedOutlined /><span className={styles.pillLabel}>CPU Usage</span></div>
          <div className={styles.pillCard}><TimelineOutlined /><span className={styles.pillLabel}>System Load Average</span></div>
          <div className={styles.pillCard}><AccountTreeOutlined /><span className={styles.pillLabel}>Process Count</span></div>
          <div className={styles.pillCard}><MemoryOutlined /><span className={styles.pillLabel}>Memory Usage</span></div>
          <div className={styles.pillCard}><ShowChartOutlined /><span className={styles.pillLabel}>Memory Commit</span></div>
          <div className={styles.pillCard}><NetworkCheckOutlined /><span className={styles.pillLabel}>Network Traffic</span></div>
          <div className={styles.pillCard}><StorageOutlined /><span className={styles.pillLabel}>Disk I/O</span></div>
          <div className={styles.pillCard}><StorageOutlined /><span className={styles.pillLabel}>Disk Utilization</span></div>
        </div>
      </section>

      {/* Output formats */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>Output Formats</span>
          <h2 className={styles.sectionTitle}>{t('guide.sar.outputTitle')}</h2>
        </header>
        <div className={styles.pillGrid}>
          <div className={styles.pillCard}><BarChartOutlined /><span className={styles.pillLabel}>{t('guide.sar.outGraph')}</span></div>
          <div className={styles.pillCard}><GridOnOutlined /><span className={styles.pillLabel}>{t('guide.sar.outCsv')}</span></div>
          <div className={styles.pillCard}><PictureAsPdfOutlined /><span className={styles.pillLabel}>{t('guide.sar.outPdf')}</span></div>
          <div className={styles.pillCard}><ImageOutlined /><span className={styles.pillLabel}>{t('guide.sar.outJpg')}</span></div>
        </div>
      </section>
    </div>
  );
};

export default SarGuide;
